import React from 'react';
import useSEO from "./useSEO";

const PharmaceuticalsStockAudit = () => {
  useSEO({
    title: "Pharmaceuticals Stock Audit | Pharma Audit Services in Hyderabad | Inventory Verification Experts",
    description:
      "Ensure compliance, accuracy, and control with professional pharmaceutical stock audit services in Hyderabad. We specialize in auditing raw materials, APIs, finished formulations, and batch inventory to meet GMP and regulatory standards.",
    keywords:
      "pharmaceutical stock audit, pharma audit services Hyderabad, pharma inventory audit, GMP compliance audit, pharmaceutical inventory verification, raw material audit pharma, finished goods audit pharma, API stock verification, pharma warehouse audit, pharma compliance audit, drug manufacturing audit, pharma ERP audit, batch inventory audit",
    ogTitle: "Pharmaceuticals Stock Audit - Hyderabad Experts",
    ogDescription:
      "Comprehensive pharmaceutical stock audits ensuring GMP compliance and accuracy across raw materials, APIs, and finished formulations.",
    ogImage: "img/pharma-stock-audit.png",
    twitterTitle: "Pharmaceuticals Stock Audit - Hyderabad Experts",
    twitterDescription:
      "Professional pharma stock audit services covering raw materials, APIs, and finished goods to ensure GMP and regulatory compliance.",
    twitterImage: "img/pharma-stock-audit.png"
  });

  return (
    <div>
      <>
        {/* Section 1 - Hero */}
        <section className="pt-lg-8 pt-xl-9">
          <div className="container position-relative pt-4 pt-lg-0">
            <div className="row align-items-center">
              {/* Text Content */}
              <div className="col-md-7 col-xl-6 mb-7 mb-md-0">
                <h1 className="mb-4 text-primary">Pharmaceuticals Stock Audit</h1>
                <p className="mb-4">
                  The <b>Pharmaceuticals Stock Audit</b> is a crucial process to
                  ensure accuracy, quality control, and compliance with GMP, WHO,
                  and regulatory standards. Our audit services help pharmaceutical
                  companies maintain strict control over their raw materials, APIs,
                  packaging materials, and finished formulations.
                </p>
                <p>
                  <b>Why Pharmaceutical Stock Audits Matter:</b>  
                  In the pharma sector, even minor stock discrepancies can lead to
                  compliance risks, production delays, or quality issues. Our audits
                  ensure precise documentation, proper valuation, and transparent
                  reporting across all materials and production stages.
                </p>
              </div>

              {/* Image */}
              <div className="col-md-5 ms-auto text-center">
                <img
                  src="imgs/pharmaceuticals-stock-audit.jpg"
                  alt="Pharmaceuticals Stock Audit"
                  className="rounded-3 shadow-sm w-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Audit Process */}
        <section className="overflow-hidden py-5 bg-light border-top">
          <div className="container">
            <div className="row align-items-center">
              {/* Image */}
              <div className="col-lg-6 text-center mb-5 mb-lg-0">
                <div className="pe-lg-6 pe-xl-8">
                  <img
                    src="imgs/pharmaceuticals-stock-audit-process.jpg"
                    alt="Pharmaceutical Audit Process"
                    className="rounded-4 shadow-sm w-100"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="col-lg-6 ms-auto">
                <h2 className="mb-lg-4">Our Pharmaceutical Audit Process</h2>
                <hr className="my-4 my-lg-5" />

                <ul className="list-group list-group-borderless">
                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Raw Material & API Verification</b>
                  </li>
                  <p>
                    Physical verification and reconciliation of Active Pharmaceutical
                    Ingredients (APIs), excipients, and raw materials with ERP
                    records and batch cards.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Batch-wise Work-in-Progress Audit</b>
                  </li>
                  <p>
                    Stage-wise verification of in-process materials, blending,
                    compression, and packing stages as per production logs.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Finished Formulation Reconciliation</b>
                  </li>
                  <p>
                    Ensuring physical stock matches batch records, testing results,
                    and warehouse balances before dispatch.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Cold Storage & Controlled Inventory Audit</b>
                  </li>
                  <p>
                    Verification of temperature-sensitive and controlled substances
                    with proper documentation and handling practices.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Packaging Material & Label Stock Review</b>
                  </li>
                  <p>
                    Checking label, blister, and carton stock for accurate usage
                    and control in accordance with GMP protocols.
                  </p>

                  <li className="list-group-item d-flex">
                    <i className="bi bi-patch-check text-primary me-2" />
                    <b>Regulatory Compliance Review</b>
                  </li>
                  <p>
                    Evaluating records for adherence to audit trails, cGMP, and
                    validation guidelines from WHO, FDA, and local authorities.
                  </p>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Benefits */}
        <section className="py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="text-primary">
                Benefits of Pharmaceutical Stock Audit
              </h2>
              <p className="lead">
                Strengthen quality control, regulatory compliance, and operational
                excellence with expert audit services.
              </p>
            </div>

            <div className="row text-center">
              <div className="col-md-4 mb-4">
                <i className="bi bi-clipboard-data text-success fs-3 mb-2"></i>
                <h5>Regulatory Compliance</h5>
                <p>
                  Ensure adherence to GMP, WHO, and FDA standards with accurate
                  inventory and audit documentation.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-box-seam text-success fs-3 mb-2"></i>
                <h5>Batch Traceability</h5>
                <p>
                  Maintain full traceability of all pharmaceutical materials
                  through proper stock recording and validation.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-cash-stack text-success fs-3 mb-2"></i>
                <h5>Cost & Waste Reduction</h5>
                <p>
                  Identify expired, obsolete, or slow-moving materials to
                  optimize inventory levels and reduce losses.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-graph-up text-success fs-3 mb-2"></i>
                <h5>Operational Efficiency</h5>
                <p>
                  Improve production planning and minimize downtime through
                  accurate stock control and reporting.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-shield-check text-success fs-3 mb-2"></i>
                <h5>Quality Assurance</h5>
                <p>
                  Verify that all materials meet quality and safety standards at
                  every stage of the production cycle.
                </p>
              </div>

              <div className="col-md-4 mb-4">
                <i className="bi bi-lightbulb text-success fs-3 mb-2"></i>
                <h5>Data Transparency</h5>
                <p>
                  Generate accurate and auditable data for management and
                  external audits, ensuring full accountability.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    </div>
  );
};

export default PharmaceuticalsStockAudit;
